import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { typographyStyles } from '../styles/typography';
import { formatDateForPDF } from './dataFormatters';
import { createT } from './simpleTranslate';
import { PDFSurveyRecordRenderer } from './contentRenderer';
import { PDFSmartFieldRenderer } from './PDFSmartFieldRenderer';
import { isSmartField } from '../../../utils/index.jsx';
import { parseSurgicalProcedureName } from '../../../utils/surgicalProcedure';
import { VPA_OUTCOME_FIELD_TYPE, vpaOutcomeLabelId } from '../../../utils/vpaOutcome';

/**
 * Unified PDF Field Renderer
 * 
 * Handles rendering of all field types in PDF documents.
 * This provides a consistent way to render fields across all PDFs.
 */

interface PDFFieldRendererProps {
  field: {
    id: string | number;
    name: string;
    label: string;
    type: string;
    order?: number;
    options?: string[] | any[];
  };
  value: any;
  locale?: string;
}

export const PDFFieldRenderer: React.FC<PDFFieldRendererProps> = ({
  field,
  value,
  locale = 'fr',
}) => {
  const t = createT(locale);

  // Handle empty/null values
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return (
      <Text style={typographyStyles.body2}>
        {isSmartField(field.type) ? t('general.no', 'Non') : '-'}
      </Text>
    );
  }

  // Handle different field types
  switch (field.type) {
    case 'textarea':
    case 'medical_history_template':
    case 'medical_history_ai':
      // HTML content - use HTML renderer
      const htmlContent = typeof value === 'string' ? value : 
        (Array.isArray(value) && value.length > 0 ? 
          value[0].medical_history_ai || value[0]['template_medical_history-notes'] || '' : '');
      
      return (
        <PDFSurveyRecordRenderer 
          recordValue={htmlContent}
          fallbackStrategy="plaintext"
        />
      );

    case 'date':
      const dateValue = typeof value === 'string' ? new Date(value) : value;
      return (
        <Text style={typographyStyles.body2}>
          {formatDateForPDF(dateValue, locale)}
        </Text>
      );

    case 'multioption':
      const options = Array.isArray(value) ? value.join(', ') : value;
      return (
        <Text style={typographyStyles.body2}>
          {options}
        </Text>
      );

    case 'surgical_procedure':
      // SHER-459: stored as { procedure, procedureId } (or a JSON string of it);
      // the PDF shows the procedure name.
      return (
        <Text style={typographyStyles.body2}>
          {parseSurgicalProcedureName(value)}
        </Text>
      );

    case VPA_OUTCOME_FIELD_TYPE: {
      // SHER-759: the VPA decision, worded like the waiting list, in the PDF's language.
      const labelId = vpaOutcomeLabelId(value);
      return (
        <Text style={typographyStyles.body2}>
          {labelId ? t(labelId) : '-'}
        </Text>
      );
    }

    case 'disposition': {
      // Parse disposition value (can be string or object)
      let dispVal = value;
      if (typeof dispVal === 'string' && dispVal.startsWith('{')) {
        try { dispVal = JSON.parse(dispVal); } catch (e) { /* keep as string */ }
      }
      const isObj = typeof dispVal === 'object' && dispVal !== null;
      const disposition = isObj ? dispVal.disposition : dispVal;
      const dateOfDeath = isObj ? dispVal.dateOfDeath : null;
      const immediateCause = isObj ? (dispVal.immediateCause || dispVal.cause) : null;
      const underlyingCause = isObj ? dispVal.underlyingCause : null;
      const placeOfDeath = isObj ? dispVal.placeOfDeath : null;
      const deathConfirmedBy = isObj ? dispVal.deathConfirmedBy : null;
      const palliativePathway = isObj ? dispVal.palliativePathway : null;

      const dispositionLabel = t(`patient.disposition.${(disposition || '').toLowerCase().replace('_', '-')}`);

      return (
        <View>
          <Text style={typographyStyles.body2}>{dispositionLabel}</Text>
          {disposition === 'DECEASED' && dateOfDeath && !isNaN(new Date(dateOfDeath).getTime()) && (
            <Text style={typographyStyles.body2}>
              {t('patient.death.date')}: {formatDateForPDF(new Date(dateOfDeath), locale)}
            </Text>
          )}
          {disposition === 'DECEASED' && immediateCause && (
            <Text style={typographyStyles.body2}>
              {t('patient.death.immediate-cause')}: {immediateCause}
            </Text>
          )}
          {disposition === 'DECEASED' && underlyingCause && (
            <Text style={typographyStyles.body2}>
              {t('patient.death.underlying-cause')}: {underlyingCause}
            </Text>
          )}
          {disposition === 'DECEASED' && placeOfDeath && (
            <Text style={typographyStyles.body2}>
              {t('patient.death.place')}: {t(`patient.death.place-${placeOfDeath.toLowerCase()}`)}
            </Text>
          )}
          {disposition === 'DECEASED' && deathConfirmedBy && (
            <Text style={typographyStyles.body2}>
              {t('patient.death.confirmed-by')}: {t(`patient.death.confirmed-by-${deathConfirmedBy.toLowerCase()}`)}
            </Text>
          )}
          {disposition === 'DECEASED' && palliativePathway !== null && palliativePathway !== undefined && (
            <Text style={typographyStyles.body2}>
              {t('patient.death.palliative-pathway')}: {palliativePathway ? t('general.yes') : t('general.no')}
            </Text>
          )}
        </View>
      );
    }

    case 'file':
      return (
        <Text style={typographyStyles.body2}>
          {t('general.file_attached', 'Fichier joint')}
        </Text>
      );

    default:
      // Handle smart fields
      if (isSmartField(field.type)) {
        return (
          <PDFSmartFieldRenderer
            type={field.type}
            value={value}
            locale={locale}
          />
        );
      }
      
      // Regular text/number fields
      return (
        <Text style={typographyStyles.body2}>
          {String(value)}
        </Text>
      );
  }
};

export default PDFFieldRenderer;

